import { useEffect, useState } from 'preact/hooks';
import { fetchPhotoUrl, type Photo } from '../../api/photos';
import type { Session } from '../../api/client';

/** A photograph's bytes as an object URL, revoked when it goes away.
 *
 *  The API authenticates with a bearer header and `<img src>` sends none, so
 *  the image has to be fetched and wrapped in a blob URL. That URL holds the
 *  decoded bytes until it is revoked — without the cleanup below, scrolling a
 *  month of days would pin every photograph seen along the way in memory.
 */
export function usePhotoUrl(session: Session, id: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let current: string | null = null;
    fetchPhotoUrl(session, id)
      .then((next) => {
        // The effect may have been torn down mid-flight; revoke immediately
        // rather than handing back a URL nothing will ever release.
        if (revoked) {
          URL.revokeObjectURL(next);
          return;
        }
        current = next;
        setUrl(next);
      })
      .catch(() => {
        // A photograph whose file is gone renders as a missing tile, not a
        // broken screen. `bujo photos gc` is what reports it to an operator.
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [session, id]);
  return url;
}

const TILE = 72;

function Tile({
  session,
  photo,
  onOpen,
}: {
  session: Session;
  photo: Photo;
  onOpen?: () => void;
}) {
  const url = usePhotoUrl(session, photo.id);
  const box = {
    width: TILE,
    height: TILE,
    flex: `0 0 ${TILE}px`,
    border: '1px solid var(--bujo-rule-hairline)',
    background: 'var(--bujo-surface-sunken)',
    overflow: 'hidden' as const,
    padding: 0,
    cursor: onOpen ? ('pointer' as const) : ('default' as const),
  };
  const inner = url ? (
    <img
      src={url}
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  ) : (
    // Never a spinner per tile: a row of them reads as breakage. A quiet frame
    // that fills in is the same wait without the alarm.
    <span aria-hidden="true" style={{ display: 'block', width: '100%', height: '100%' }} />
  );
  if (!onOpen) return <div style={box}>{inner}</div>;
  return (
    <button type="button" onClick={onOpen} style={box} aria-label="Open photograph">
      {inner}
    </button>
  );
}

/** A row of photographs. Read-only: adding and removing happen in the editor,
 *  which is how the reflection text already behaves. */
export function PhotoStrip({
  session,
  photos,
  onOpen,
}: {
  session: Session;
  photos: Photo[];
  onOpen?: (photo: Photo) => void;
}) {
  if (photos.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--bujo-space-2)',
        paddingTop: 'var(--bujo-space-2)',
        // Photographs are a row, not a grid that reflows the day beneath them.
        overflowX: 'auto',
      }}
    >
      {photos.map((photo) => (
        <Tile
          key={photo.id}
          session={session}
          photo={photo}
          onOpen={onOpen ? () => onOpen(photo) : undefined}
        />
      ))}
    </div>
  );
}

/** One photograph, full-bleed, dismissed by tapping anywhere.
 *
 *  `onRemove` is passed from the editor and withheld everywhere else, so a
 *  photograph is removable exactly where it is added — the same rule the
 *  reflection text already follows.
 */
export function PhotoViewer({
  session,
  photo,
  onDismiss,
  onRemove,
}: {
  session: Session;
  photo: Photo;
  onDismiss: () => void;
  onRemove?: () => void;
}) {
  const url = usePhotoUrl(session, photo.id);
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photograph"
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        // Above the reflection sheet, which is 40.
        zIndex: 60,
        background: 'var(--bujo-surface-page)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--bujo-gutter)',
        cursor: 'pointer',
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <span
          style={{
            font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
            color: 'var(--bujo-text-meta)',
          }}
        >
          This photograph is missing.
        </span>
      )}
      {onRemove ? (
        <button
          type="button"
          onClick={(event) => {
            // The backdrop dismisses on click; removing must not also be a
            // dismissal, or the confirmation below never gets a chance.
            event.stopPropagation();
            if (confirmed) {
              onRemove();
            } else {
              setConfirmed(true);
            }
          }}
          style={{
            position: 'absolute',
            left: 'calc(var(--bujo-gutter) + env(safe-area-inset-left))',
            right: 'calc(var(--bujo-gutter) + env(safe-area-inset-right))',
            bottom: 'calc(var(--bujo-gutter) + env(safe-area-inset-bottom))',
            minHeight: 'var(--bujo-tap-min)',
            background: 'var(--bujo-surface-page)',
            border: `1px solid ${confirmed ? 'var(--bujo-state-error)' : 'var(--bujo-rule-hairline)'}`,
            color: confirmed ? 'var(--bujo-state-error)' : 'var(--bujo-text-meta)',
            font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
            letterSpacing: 'var(--bujo-ls-label)',
            cursor: 'pointer',
          }}
        >
          {/* Two taps, not a browser confirm: deleting the only copy of a
              photograph is not undoable, and the second tap says so. */}
          {confirmed ? 'REMOVE · THIS CANNOT BE UNDONE' : 'REMOVE'}
        </button>
      ) : null}
    </div>
  );
}
